import { defaultSlateValue } from "./text/default-slate-value"
import { sectionDefaults } from "app/regions/blocks/section/section-defaults"

// 1. Import your block here:

import { Test } from "./test/test-block"
import { Text } from "./text/text"
import { TextEmail } from "./text/text-email"
import { Gallery } from "./gallery/gallery"
import { Gallery as GalleryEmail } from "./gallery/gallery.email"
import { Image } from "./gallery/image"
import { Image as ImageEmail } from "./gallery/image.email"
import { Icons } from "./icons/icons"
import { BlockIcon as Icon } from "./icons/icon"
import { Links } from "./links/links"
import { Links as LinksEmail } from "./links/links.email"
import { Link } from "./links/link"
import { BandsInTown as Bandsintown } from "./bandsintown/bandsintown"
import { Bandsintown as BandsintownEmail } from "./bandsintown/bandsintown.email"
import { Section } from "./section/section"
import { Layout } from "./layout/layout"
import { ProductEmbedEditor } from "./product-embed/product-embed-editor"
import { ProductEmbedEmail } from "./product-embed/product-embed-email"

// 2. Import your block's settings here:

import { TestSettings } from "./test/test-settings"
import { TextSettings } from "./text/text-settings"
import { GallerySettings } from "./gallery/gallery-settings"
import { ImageSettings } from "./gallery/image-settings"
import { IconsSettings } from "./icons/icons-settings"
import { IconSettings } from "./icons/icon-settings"
import { LinksSettings } from "./links/links-settings"
import { LinkSettings } from "./links/link-settings"
import { BandsInTownSettings as BandsintownSettings } from "./bandsintown/bandsintown-settings"
import { SectionSettings } from "./section/section-settings"
import { switchToType as LayoutSettings } from "./layout/layout-settings"
import { ProductEmbedSettings } from "./product-embed/product-embed-settings"
import { CreatorProfileProductsSettings } from "./creator-profile/products-settings"

// 3. Import the reorderable preview for your block here:

import { TestPreview } from "./test/test-preview"
import { TextPreview } from "./text/text-preview"
import { GalleryPreview } from "./gallery/gallery-preview"
import { ImagePreview } from "./gallery/image-preview"
import { IconsPreview } from "./icons/icons-preview"
import { IconPreview } from "./icons/icon-preview"
import { LinksPreview } from "./links/links-preview"
import { LinkPreview } from "./links/link-preview"
import { BandsInTownPreview } from "./bandsintown/bandsintown-preview"
import { SectionPreview } from "./section/section-preview"
import { LayoutPreview } from "./layout/layout-preview"
import { ProductEmbedPreview } from "./product-embed/product-embed-preview"

// 6. Add an object that describes your block to this array. It should be
//    in the shape:
//
//    {
//      type: "example",                // a unique identifier for your block
//      component: Example,             // the component you imported above
//      settings: ExampleSettings,      // the settings you imported above
//      preview: ExamplePreview,        // the reordering preview imported above
//      icon: "some-icon",              // the icon you imported above
//      childType: "ExampleChild"       // the `type` of child elements
//      label: "Example",               // optional; for the reorderable preview
//      defaults: { ... },              // optional; block's default settings
//      defaultChildren: [              // optional; block's default children
//        { type: "ExampleChild", ... }
//      ]
//    }

export const blocks = [
  {
    type: "test",
    component: Test,
    settings: TestSettings,
    preview: TestPreview,
    label: "Test",
    defaults: {
      text: "test block"
    }
  },
  {
    type: "text",
    component: Text,
    email: TextEmail,
    settings: TextSettings,
    preview: TextPreview,
    label: "Text",
    defaults: {
      value: defaultSlateValue
    }
  },
  {
    type: "gallery",
    component: Gallery,
    email: GalleryEmail,
    settings: GallerySettings,
    preview: GalleryPreview,
    label: "Gallery",
    icon: "image-gallery",
    menuIcon: "gallery",
    childType: "image",
    defaults: {
      columns: 2,
      radius: 6,
      caption_size: 15,
      caption_font_weight: "normal",
      caption_color: "#000000",
      crop: "landscape"
    }
  },
  {
    type: "image",
    component: Image,
    email: ImageEmail,
    settings: ImageSettings,
    preview: ImagePreview,
    label: "Image",
    icon: "image",
    menuIcon: "image",
    defaults: {}
  },
  {
    type: "icons",
    component: Icons,
    settings: IconsSettings,
    preview: IconsPreview,
    label: "Social Icons",
    icon: "globe",
    menuIcon: "socialIcons",
    childType: "icon",
    defaults: {
      alignment: "left",
      foreground: null,
      background: null,
      shape: "square"
    }
  },
  {
    type: "icon",
    component: Icon,
    settings: IconSettings,
    preview: IconPreview,
    label: "Icon",
    icon: "globe",
    menuIcon: "image",
    defaults: {
      icon: "twitter",
      url: "https://kit.com"
    }
  },
  {
    type: "links",
    component: Links,
    email: LinksEmail,
    settings: LinksSettings,
    preview: LinksPreview,
    label: "Links",
    icon: "link",
    menuIcon: "links",
    childType: "link",
    defaults: {
      backgroundColor: "#f7f7f7",
      textColor: "#2d2d2d",
      fontSize: 16,
      fontWeight: "normal",
      borderRadius: 4,
      linkBorderWidth: 1,
      linkBorderColor: "#e3e3e3",
      useIcons: true,
      iconColor: false,
      iconBorderWidth: 0,
      iconBorderColor: "#e3e3e3"
    }
  },
  {
    type: "link",
    component: Link,
    settings: LinkSettings,
    preview: LinkPreview,
    label: "Link",
    icon: "link",
    menuIcon: "link",
    defaults: {
      icon: "external-link",
      url: "",
      text: ""
    }
  },
  {
    type: "bandsintown",
    component: Bandsintown,
    email: BandsintownEmail,
    settings: BandsintownSettings,
    preview: BandsInTownPreview,
    label: "Bandsintown",
    icon: "music-note",
    menuIcon: "bandsintown",
    defaults: {
      name: "",
      numberOfEvents: "all",
      startDate: "",
      endDate: "",
      events: null,
      textColor: "#2d2d2d"
    }
  },
  {
    type: "section",
    component: Section,
    settings: SectionSettings,
    preview: SectionPreview,
    label: "Section",
    defaults: sectionDefaults
  },
  {
    type: "layout",
    component: Layout,
    settings: LayoutSettings,
    preview: LayoutPreview,
    label: "Layout",
    icon: "layout-masonry",
    menuIcon: "layout",
    defaults: {
      template: null
    }
  },
  {
    type: "productEmbed",
    component: ProductEmbedEditor,
    settings: ProductEmbedSettings,
    email: ProductEmbedEmail,
    preview: ProductEmbedPreview,
    label: "Product",
    icon: "tag-money",
    menuIcon: "money",
    defaults: {
      // Each of these is accessible on the `properties` object from
      // `useBlocks`. These are typically used for the settings you see in the
      // sidebar. They're saved to the server, so they'll persist after page
      // reloads. Here, we're just trying to define some good defaults.
      template: "ImageLeftCTARight", // in case we support multiple templates in the future
      productId: null,
      includeDiscount: false,
      discountCode: null,
      goDirectlyToCheckout: false,
      showPrice: true,
      showDescription: true,
      showImage: true,
      showButton: true,
      buttonText: "",
      buttonColor: "#65dc8d",
      buttonTextColor: "#FFFFFF"
    }
  },
  {
    type: "creatorProfileProducts",
    settings: CreatorProfileProductsSettings,
    label: "Products",
    component: () => null // This is added since we don't need a component for this block
  }
]

